/**
 * FilterLeftPicker
 * Menu de seleção que abre à esquerda do trigger (ideal para sidebars de filtro).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check } from 'lucide-react'
import './FilterLeftPicker.css'

const MENU_WIDTH = 288
const HOVER_CLOSE_DELAY = 280

function FilterLeftPicker({
  label,
  title,
  value = '',
  options = [],
  pinnedOptions = [],
  onChange,
  placeholder = 'Selecionar...',
  emptyOptionLabel = 'Todos',
  searchable = false,
  searchPlaceholder = 'Buscar...',
  disabled = false,
  allowClear = true,
  openOnHover = false
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [menuPosition, setMenuPosition] = useState(null)
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const closeTimerRef = useRef(null)

  const allOptions = useMemo(
    () => [...pinnedOptions, ...options],
    [pinnedOptions, options]
  )

  const selectedOption = allOptions.find((opt) => String(opt.value) === String(value))
  const displayLabel =
    selectedOption?.label ||
    (allowClear && value === '' ? emptyOptionLabel : placeholder)
  const hasSelection = value !== '' && value != null

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return options
    return options.filter((opt) => String(opt.label).toLowerCase().includes(term))
  }, [options, search])

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const closeMenu = useCallback(() => {
    clearCloseTimer()
    setOpen(false)
    setSearch('')
  }, [clearCloseTimer])

  const openMenu = useCallback(() => {
    if (disabled) return
    clearCloseTimer()
    setOpen(true)
  }, [disabled, clearCloseTimer])

  const scheduleClose = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(closeMenu, HOVER_CLOSE_DELAY)
  }, [clearCloseTimer, closeMenu])

  useEffect(() => {
    if (!open) {
      setMenuPosition(null)
      return undefined
    }

    const updatePosition = () => {
      if (!triggerRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      const left = Math.max(8, rect.left - MENU_WIDTH)
      const maxHeight = Math.min(320, window.innerHeight - rect.top - 16)
      setMenuPosition({
        top: rect.top,
        left,
        maxHeight: Math.max(160, maxHeight)
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    const handleClickOutside = (event) => {
      if (openOnHover) return
      const inRoot = rootRef.current?.contains(event.target)
      const inMenu = menuRef.current?.contains(event.target)
      if (!inRoot && !inMenu) closeMenu()
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeMenu()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      clearCloseTimer()
    }
  }, [open, openOnHover, closeMenu, clearCloseTimer])

  const handleSelect = (nextValue) => {
    onChange?.(nextValue)
    closeMenu()
  }

  const menuOptions = useMemo(() => {
    const base = allowClear ? [{ value: '', label: emptyOptionLabel }] : []
    const pinned = search.trim() ? pinnedOptions.filter((opt) =>
      String(opt.label).toLowerCase().includes(search.trim().toLowerCase())
    ) : pinnedOptions
    return [...base, ...pinned, ...filteredOptions]
  }, [allowClear, emptyOptionLabel, pinnedOptions, filteredOptions, search])

  return (
    <div
      className="filter-left-picker"
      ref={rootRef}
      onMouseEnter={openOnHover ? openMenu : undefined}
      onMouseLeave={openOnHover ? scheduleClose : undefined}
    >
      {label && <label className="filter-left-picker-label">{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        className={[
          'filter-left-picker-trigger',
          open ? 'is-open' : '',
          hasSelection ? 'has-value' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (disabled) return
          if (openOnHover) {
            openMenu()
            return
          }
          setOpen((prev) => !prev)
        }}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span
          className={[
            'filter-left-picker-trigger-label',
            !hasSelection ? 'is-placeholder' : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {displayLabel}
        </span>
        <ChevronDown
          size={16}
          className={`filter-left-picker-trigger-icon${open ? ' is-open' : ''}`}
          aria-hidden
        />
      </button>

      {open && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="filter-left-picker-menu filter-left-picker-menu--fixed"
          role="listbox"
          aria-label={title || label}
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: MENU_WIDTH,
            maxHeight: menuPosition.maxHeight
          }}
          onMouseEnter={openOnHover ? clearCloseTimer : undefined}
          onMouseLeave={openOnHover ? scheduleClose : undefined}
        >
          <div className="filter-left-picker-menu-header">
            {title && <p className="filter-left-picker-menu-title">{title}</p>}
            {searchable && (
              <input
                type="text"
                className="filter-left-picker-search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus={!openOnHover}
              />
            )}
          </div>
          <ul className="filter-left-picker-options">
            {menuOptions.length === 0 ? (
              <li className="filter-left-picker-empty">Nenhum resultado encontrado</li>
            ) : (
              menuOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value)
                return (
                  <li key={String(opt.value || '__all__')}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`filter-left-picker-option${isSelected ? ' is-selected' : ''}${opt.className ? ` ${opt.className}` : ''}`}
                      onClick={() => handleSelect(opt.value)}
                    >
                      <span>{opt.label}</span>
                      {isSelected && <Check size={14} className="filter-left-picker-option-check" />}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  )
}

export default FilterLeftPicker
